import { array } from 'fp-ts';
import { flow } from 'fp-ts/function';
import { get, identity } from 'lodash/fp';
import { getContainer } from '@mews-commander/legacy-app-instance';
import { Day, TimeUnit } from '@mews/enums';
import { unlessIsNil } from '@mews/fp';
import {
    DateService,
    DateTime,
    DateTimeLocalizationInfo,
    DateTimeService,
    DateTimeUtc,
    DateTimeUtcService,
    DateTimeUtils,
    DurationService,
    DurationUtils,
    TimeService,
    TimeUnitUtils,
} from '@mews/framework';
import { Interval } from '@mews-commander/types';

export * from './datesTS';

export const getDateTime = ({
    timezone,
    localizationInfo,
    localizedDate,
    localizedTime,
}: {
    timezone: string;
    localizationInfo: DateTimeLocalizationInfo;
    localizedDate: string;
    localizedTime: string;
}) => {
    const parsedDate = DateService.fromString(localizedDate, localizationInfo, timezone);
    const parsedTime = TimeService.fromShortString(localizedTime, localizationInfo);
    return DateTimeService.fromDateAndTime(parsedDate, parsedTime);
};

export const getDateTimeUtc = ({
    timezone,
    localizationInfo,
    date,
    time,
}: {
    timezone: string;
    localizationInfo: DateTimeLocalizationInfo;
    date: string;
    time: string;
}) => {
    const dateTime = getDateTime({ timezone, localizationInfo, localizedDate: date, localizedTime: time });
    return DateTimeService.toUtc(dateTime).value;
};

export const getDurationAsMilliseconds = flow(DurationService.parse, DurationService.asMilliseconds);

export const getDurationAsDays = flow(DurationService.parse, DurationService.asDays);
export const getDurationAsTimeUnit = (timeUnit: $TSFixMe) => flow(DurationService.parse, DurationService.as(timeUnit));
export const getExceptionDuration = (timeUnit: TimeUnit) =>
    flow(
        DurationService.parse,
        (duration) => DurationService.toObject(duration, { partial: true }),
        (obj) => obj[DurationUtils.timeUnitToDurationObjectKey(timeUnit)]
    );

/** @deprecated use DateTimeUtils.dateTimeUtcStringToDateString */
export const utcToLocalizedDate = (utcDateString: $TSFixMe, timezone: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    return flow(DateTimeService.fromUtcString, appLocalizationComponent.localizeDate)(utcDateString, timezone);
};

/** @deprecated use DateTimeUtils.dateTimeUtcStringToDateTimeString */
export const utcToLocalizedDateTime = (utcDateString: $TSFixMe, timezone: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    return flow(DateTimeService.fromUtcString, appLocalizationComponent.localizeDateTime)(utcDateString, timezone);
};

/** @deprecated */
export const dateTimeToLocalizedDate = (dateTime: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    return appLocalizationComponent.localizeDate(dateTime);
};

export const dateTimeToDayName = flow(DateTimeService.getDay, Day.getByValue, get('name'));

/**
 * This would only work on dateTime with stripped time
 */
export const isDateTimeToday = ({ dateTime, now }: { dateTime: DateTime; now: DateTime }) => {
    const dateTimeToday = DateTimeService.startOfDay(now);
    return DateTimeService.isEqual(dateTime, dateTimeToday);
};

export const isDateTimeCurrentMonth = ({ dateTime, now }: { dateTime: DateTime; now: DateTime }) => {
    const dateTimeToday = DateTimeService.startOfDay(now);

    const currentYearMonth = DateTimeService.toYearMonth(dateTimeToday);
    const subjectYearMonth = DateTimeService.toYearMonth(dateTime);
    return currentYearMonth === subjectYearMonth;
};

export const isDateTimeWeekendDay = flow(
    dateTimeToDayName,
    (day) => day != null && [Day.Sunday.name, Day.Saturday.name].includes(day)
);

/** @deprecated */
export const localizedDateStringToDate = (dateString: $TSFixMe) => {
    const container = getContainer();
    const { appLocalizationComponent } = container();
    return flow(appLocalizationComponent.parseDate, DateTimeService.toDate)(dateString);
};

/** @deprecated */
export const createSafeToUtcDateStringConverter = (parser: $TSFixMe) =>
    unlessIsNil(flow(parser, DateTimeService.toUtc, get('value')));

/** @deprecated */
export const safeConvertDateTimeToUtcDateString = createSafeToUtcDateStringConverter(identity);

export const createTimeUnitRangeArray = ({ startDateTime, endDateTime, timeUnit, getRealHours }: $TSFixMe) => {
    const correctedTimeUnit = timeUnit === TimeUnit.Hour && !getRealHours ? TimeUnit.Day : timeUnit;
    const timePart = TimeUnitUtils.timeUnitToDateTimePart(timeUnit);
    const timeUnitStartDateTime = DateTimeService.startOf(correctedTimeUnit)(startDateTime);
    const timeUnitEndDateTime = DateTimeService.endOf(correctedTimeUnit)(endDateTime);
    const timeUnitCount = TimeUnitUtils.dateTimeToTimeUnitCount({ timePart, greedy: true })(
        timeUnitStartDateTime,
        timeUnitEndDateTime
    );
    return array.makeBy(timeUnitCount, (i) => DateTimeService.add(timeUnit)(timeUnitStartDateTime, i));
};

/** @deprecated */
export const getStringFromDate = (date: $TSFixMe) => {
    const container = getContainer();
    const {
        appLocalizationComponent: { getCurrentDateTimeLocalizationInfo },
    } = container();
    return DateService.toString(date, getCurrentDateTimeLocalizationInfo());
};

/** @deprecated */
export const getShortStringFromTime = (time: $TSFixMe) => {
    const container = getContainer();
    const {
        appLocalizationComponent: { getCurrentDateTimeLocalizationInfo },
    } = container();
    return TimeService.toShortString(time, getCurrentDateTimeLocalizationInfo());
};

/**
 * @example 2023-08-22T06:12:58.590Z  ->  8/22/2023
 * @deprecated use getDateStringFromUtc selector or DateTimeUtils.dateTimeUtcStringToDateString
 */
export const utcToDateString = flow(DateTimeUtils.dateTimeUtcStringToDate, getStringFromDate);

/** @deprecated use getShortTimeStringFromUtc selector */
export const utcToShortTimeString = flow(DateTimeUtils.dateTimeUtcStringToTime, getShortStringFromTime);

/** @deprecated use this as escape hatch for mismatched utc format serialization only */
export const normalizeUtcFormat = flow(DateTimeUtcService.parse, DateTimeUtcService.toISOString);

export const doesUtcIntervalContain = <A extends Interval, B extends Interval>(
    boundingInterval: A,
    intervalToCheck: B
) =>
    (boundingInterval.startUtc >= intervalToCheck.startUtc && boundingInterval.startUtc < intervalToCheck.endUtc) ||
    (boundingInterval.endUtc > intervalToCheck.startUtc && boundingInterval.endUtc < intervalToCheck.endUtc) ||
    (intervalToCheck.startUtc >= boundingInterval.startUtc && intervalToCheck.startUtc < boundingInterval.endUtc);

export const dateTimeToUtc = flow(DateTimeService.toUtc, DateTimeUtcService.toISOString);

export const isEqualMonthAndYear = (dateTime1: $TSFixMe, dateTime2: $TSFixMe) =>
    dateTime1.value.substr(0, 7) === dateTime2.value.substr(0, 7);

export const addDaysUtc = ({ utc, timezone, dayCount }: $TSFixMe) => {
    const dateTimeUtc = DateTimeUtcService.parse(utc);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const addedDays = DateTimeService.addDays(dateTime, dayCount);
    return DateTimeUtcService.toISOString(DateTimeService.toUtc(addedDays));
};

export const addYearsUtc = ({ utc, timezone, yearCount }: { utc: string; timezone: string; yearCount: number }) => {
    const dateTimeUtc = DateTimeUtcService.parse(utc);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const addedYears = DateTimeService.addYears(dateTime, yearCount);
    return DateTimeUtcService.toISOString(DateTimeService.toUtc(addedYears));
};

/** @deprecated */
export const getTomorrowLocalizedDate = (utc: $TSFixMe, timezone: $TSFixMe) =>
    utcToLocalizedDate(addDaysUtc({ utc, timezone, dayCount: 1 }), timezone);

export const getDateFromDateTimeUtc = (dateTimeUtc: $TSFixMe, timezone: $TSFixMe) =>
    flow(DateTimeService.fromUtc, DateTimeService.toDate)(dateTimeUtc, timezone);

export const utcToStartOfMonthUtc = (timezone: $TSFixMe) =>
    flow(
        DateTimeUtcService.parse,
        (utcDateTime) => DateTimeService.fromUtc(utcDateTime, timezone),
        DateTimeService.startOfMonth,
        dateTimeToUtc
    );

interface MonthLocalizedDateParams {
    utc: string;
    timezone: string;
    monthCount: number;
}

const addMonthsUtc = ({ utc, timezone, monthCount }: MonthLocalizedDateParams) => {
    const dateTimeUtc = DateTimeUtcService.parse(utc);
    const dateTime = DateTimeService.fromUtc(dateTimeUtc, timezone);
    const addedMonths = DateTimeService.addMonths(dateTime, monthCount);
    return DateTimeUtcService.toISOString(DateTimeService.toUtc(addedMonths));
};

/** @deprecated */
export const getAddedMonthsLocalizedDate = ({ utc, timezone, monthCount }: MonthLocalizedDateParams) =>
    utcToLocalizedDate(addMonthsUtc({ utc, timezone, monthCount }), timezone);

export const isDateTimeUtcOlderThan23Hours = (appNowUtc: DateTimeUtc, dateTimeUtcValue: string) => {
    const serverUtc = DateTimeUtcService.parse(dateTimeUtcValue);
    const sessionExpiresAtUtc = DateTimeUtcService.addHours(serverUtc, 23);
    return DateTimeUtcService.isGreater(appNowUtc, sessionExpiresAtUtc);
};
